// Admin → Vendors → Payouts: the payouts as they are made by hand (a bank
// transfer, or Stripe's Send money) — where the money stands (cards), the
// vendors ready to pay (their payouts created here), each payout's transfer
// (the account, the amount, the message; then its reference and "Mark paid"),
// the vendors still under their minimum, and the history. A vendor's own
// payouts, contract and ledger are on its page (Payouts tab).
// The payout reports for the accountant: the reports issued
// in a month (quarter, year), as a CSV or a ZIP of the CSV and the PDFs.
import React, { useEffect, useRef, useState } from "react"
import moment from "moment"
import Button from "aldoo-ra/Button"
import DateFilterDropdown from "aldoo-ra/DateFilterDropdown"
import DataTable from "aldoo-ra/DataTable"
import Tabs from "aldoo-ra/Tabs"
import { useUrlParam } from "aldoo-ra/URLState"
import { showSuccessToast, showErrorToast } from "aldoo-ra/Toast"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { Section, Empty, Chip, TextInput, money, dateOnly } from "../AdminShared/ui"
import { PayoutsAdmin, PayoutsList } from "../AdminShared/api"
import usePortalTexts from "../Vendor/texts/usePortalTexts"
import { PAYOUTS } from "../Vendor/texts/bundles"
import InvoicePdf, { PayoutDocuments } from "./InvoicePdf"
import { downloadFile } from "./pdf"
import useStoreEvents from "aldoo-ra/Store/useStoreEvents"

// why a vendor is not paid (aldoo-na store/payouts/runs.js blocker / skipped)
export const REASON = { no_signed_contract: "no signed contract", payout_details_incomplete: "payout details incomplete", nothing_to_pay: "nothing to pay (balance ≤ 0)", already_paid: "already paid this month", claimed_by_another_run: "taken by a payout made at the same moment", below_minimum: "below the vendor's minimum payout", charge_needs_review: "owes Aldoo — waiting for your review (Charges to review)", already_settled: "already charged" }
const CHIP = { created: "bg-blue-100 text-blue-800", processing: "bg-blue-100 text-blue-800", posted: "bg-green-100 text-green-800", failed: "bg-red-100 text-red-800", returned: "bg-red-100 text-red-800", canceled: "bg-gray-200 text-gray-700" }

// last month: what the accountant asks for on the payout day
const lastMonth = () => {
  const start = moment().subtract(1, "month").startOf("month")
  return { type: "custom", mode: "month", range: { startDate: start.toDate(), endDate: start.clone().endOf("month").toDate() } }
}
const dayOf = (date) => moment(date).format("YYYY-MM-DD")

/** The payout reports issued in a period, downloaded for the accountant: a CSV, or a ZIP of it and the PDFs. */
function InvoicesExport() {
  const [period, setPeriod] = useState(lastMonth)
  const [busy, setBusy] = useState("")
  const download = async (format) => {
    if (!period?.range?.startDate || !period?.range?.endDate) return showErrorToast("Pick a period")
    setBusy(format)
    const res = await PayoutsAdmin("invoices_export", { from: dayOf(period.range.startDate), to: dayOf(period.range.endDate), format }).catch((e) => ({ error: e?.message }))
    setBusy("")
    if (res?.error) return showErrorToast(res.error)
    if (!res?.count) return showErrorToast("No payout reports in this period")
    if (downloadFile(res)) showSuccessToast(`${res.count} payout report${res.count === 1 ? "" : "s"} exported`)
  }
  return (
    <Section title="Payout reports for the accountant" subtitle="The payout reports issued in the period: a CSV (number, date, vendor, company ID, VAT number, country, currency, commission, sales, refunds, payout amount, payout ID, Stripe trace ID), or a ZIP with the CSV and every PDF">
      <div className="flex flex-wrap items-center gap-2" data-testid="invoices-export">
        <div className="w-full sm:w-[320px]">
          <DateFilterDropdown variant="admin" value={period} onChange={setPeriod} showLabel={false} disableLifetime modes={["month", "quarter", "year", "range"]} showArrows />
        </div>
        <Button variant="admin_secondary" disabled={!!busy} onClick={() => download("csv")} data-testid="invoices-export-csv">{busy === "csv" ? "Exporting…" : "Export CSV"}</Button>
        <Button variant="admin_primary" disabled={!!busy} onClick={() => download("zip")} data-testid="invoices-export-zip">{busy === "zip" ? "Exporting…" : "Export ZIP (CSV + PDFs)"}</Button>
      </div>
    </Section>
  )
}

const STATUS_LABEL = { created: "Transfer to make", processing: "Being sent (Stripe)", posted: "Paid", failed: "Failed", returned: "Returned", canceled: "Canceled" }

// copied to the clipboard, said so
const copy = (text, what) => navigator.clipboard?.writeText(String(text)).then(() => showSuccessToast(`${what} copied`))

// a row for the tables: an id (the plan's rows have none) and what a search matches in it
const withSearch = (row, ...words) => ({
  ...row,
  _id: row._id || `${row.orgId}-${row.currency}-${row.blocker || ""}`,
  _search: [...words, money(row.amount, row.currency), ((row.amount || 0) / 100).toFixed(2), ((row.amount || 0) / 100).toFixed(2).replace(".", ",")].filter(Boolean).join(" ").toLowerCase(),
})

/** A tab's name with how many wait in it, a coloured count (none at 0). */
const TabLabel = ({ text, count, tone }) => (
  <span className="inline-flex items-center gap-2">
    {text}
    {count > 0 && <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold leading-5 text-center tabular-nums ${tone}`}>{count}</span>}
  </span>
)

/** A tab's list: the framework's table, searched and paged, its rows drawn by `renderRow`. */
function Table({ rows, renderRow, placeholder, empty, itemsPerPage = 10 }) {
  return (
    <DataTable
      variant="admin"
      // the search as wide as the rows (the variant leaves room for a title beside it)
      styleOverrides={{ searchInput: "text-admin_text w-full h-auto py-1" }}
      data={rows}
      itemIdField="_id"
      itemsPerPage={itemsPerPage}
      searchField="_search"
      searchPlaceholder={placeholder}
      scrollFades={false}
      mobileCards={false}
      renderRow={renderRow}
      noData={<Empty>{empty}</Empty>}
    />
  )
}

/** A figure at the top: how many, how much, what it means. */
function SummaryCard({ label, count, amount, currency, sub, tone = "", unit = "vendor", testId }) {
  return (
    <div className={`flex-1 min-w-[170px] rounded-lg border bg-admin_paper p-3 ${tone || "border-admin_bordered"}`} data-testid={testId}>
      <div className="text-xs text-admin_text_secondary">{label}</div>
      <div className="mt-0.5 flex items-baseline gap-2">
        <span className="text-xl font-bold tabular-nums">{money(amount || 0, currency || "eur")}</span>
        <span className="text-xs text-admin_text_secondary">{count} {count === 1 ? unit : `${unit}s`}</span>
      </div>
      {sub && <div className="text-xs text-admin_text_secondary mt-0.5">{sub}</div>}
    </div>
  )
}

/**
 * A payout waiting for its bank transfer (manual): the three steps in order — the account and the
 * amount to send, the message the transfer carries, and once sent, the bank's reference and
 * "Mark paid" (the report is issued and the vendor told), or "Failed" (its sales go back to the next payout).
 */
function TransferCard({ payout, vendor, onDone, onOpenVendor }) {
  const [iban, setIban] = useState("")
  const [reference, setReference] = useState("")
  const [busy, setBusy] = useState("")
  const showIban = async () => {
    const res = await PayoutsAdmin("reveal_iban", { orgId: String(payout.orgId) }).catch(() => null)
    if (!res?.iban) return showErrorToast("No IBAN on file for this vendor")
    setIban(res.iban)
  }
  const act = async (op, extra) => {
    setBusy(op)
    const res = await PayoutsAdmin(op, { payoutId: payout._id, ...extra }).catch((e) => ({ error: e?.message }))
    setBusy("")
    if (res?.error) return showErrorToast(res.error)
    showSuccessToast(op === "mark_posted" || op === "connect_transfer" ? `${op === "connect_transfer" ? "Sent to their Stripe account" : "Marked paid"} — report ${res.payout?.invoiceNumber || ""} issued, the vendor told` : "Marked failed — its sales wait for the next payout")
    onDone?.()
  }
  const markPaid = () =>
    showPromptDialog({
      variant: "admin",
      title: `Mark ${money(payout.amount, payout.currency)} to ${vendor} as paid?`,
      message: "Only once the bank transfer is made: the vendor's payout report is issued and they are emailed.",
      confirmText: "Mark paid",
      cancelText: "Cancel",
      onConfirm: () => act("mark_posted", { reference: reference.trim() }),
    })
  // a vendor paid to its own connected Stripe account: one step, the transfer from Aldoo's Stripe balance
  const sendToStripe = () =>
    showPromptDialog({
      variant: "admin",
      title: `Send ${money(payout.amount, payout.currency)} to ${vendor}'s Stripe account?`,
      message: "A transfer from Aldoo's Stripe balance to their connected account, made now: the payout is marked paid, their report issued and they are emailed. The balance must have the amount available.",
      confirmText: "Send",
      cancelText: "Cancel",
      onConfirm: () => act("connect_transfer"),
    })
  const step = (n, title, children) => (
    <div className="flex gap-3">
      <span className="shrink-0 w-6 h-6 rounded-full bg-admin_primary/10 text-admin_primary text-xs font-bold flex items-center justify-center">{n}</span>
      <div className="flex-1 min-w-0 flex flex-col gap-1">
        <div className="text-sm font-semibold">{title}</div>
        {children}
      </div>
    </div>
  )
  return (
    <div className="rounded-lg border border-admin_primary/40 bg-admin_paper p-4 flex flex-col gap-3" data-testid="transfer-card">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <button type="button" className="text-base font-semibold hover:underline" onClick={() => onOpenVendor?.(String(payout.orgId))}>{vendor}</button>
        <Chip className={CHIP[payout.status] || "bg-admin_neutral_1"}>{STATUS_LABEL[payout.status] || payout.status}</Chip>
        <span className="text-xs text-admin_text_secondary">created {dateOnly(payout.runAt || payout.createdAt)}</span>
        <span className="flex-1" />
        <span className="text-xl font-bold tabular-nums">{money(payout.amount, payout.currency)}</span>
      </div>
      {payout.method === "stripe_connect" ? (
        step(1, "Send to their Stripe account", (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-admin_text_secondary">To</span>
            <span className="font-mono">{payout.stripeAccountId || "their connected account"}</span>
            <span className="text-admin_text_secondary">amount</span>
            <b className="tabular-nums">{money(payout.amount, payout.currency)}</b>
            <Button variant="admin_primary" className="h-7 px-3 text-xs" disabled={!!busy} onClick={sendToStripe} data-testid="transfer-connect-send">{busy === "connect_transfer" ? "Sending…" : "Send"}</Button>
            <Button variant="admin_secondary" className="h-7 px-2 text-xs" disabled={!!busy} onClick={() => act("mark_failed", { reason: "Transfer failed or not made" })} data-testid="transfer-failed">Failed</Button>
            <span className="w-full text-xs text-admin_text_secondary">The vendor connected their own Stripe account: the payout is a transfer from Aldoo's Stripe balance, with the message "{payout.transferMessage || "Aldoo Sales"}".</span>
          </div>
        ))
      ) : (
      <>
      {step(1, "Send the money", (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-admin_text_secondary">To</span>
          <span className="font-mono">{iban || `IBAN •••• ${payout.ibanLast4 || "????"}`}</span>
          {iban ? (
            <Button variant="admin_secondary" className="h-7 px-2 text-xs" onClick={() => copy(iban, "IBAN")}>Copy IBAN</Button>
          ) : (
            <Button variant="admin_secondary" className="h-7 px-2 text-xs" onClick={showIban} data-testid="transfer-show-iban">Show IBAN</Button>
          )}
          <span className="text-admin_text_secondary">amount</span>
          <b className="tabular-nums">{money(payout.amount, payout.currency)}</b>
          <Button variant="admin_secondary" className="h-7 px-2 text-xs" onClick={() => copy((payout.amount / 100).toFixed(2), "Amount")}>Copy amount</Button>
          <span className="w-full text-xs text-admin_text_secondary">Stripe dashboard → Payouts → Send money (the vendor is a recipient there), or your bank.</span>
        </div>
      ))}
      {step(2, "With this message", (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-mono">{payout.transferMessage || "Aldoo Sales"}</span>
          <Button variant="admin_secondary" className="h-7 px-2 text-xs" onClick={() => copy(payout.transferMessage || "Aldoo Sales", "Message")} data-testid="transfer-copy-message">Copy message</Button>
        </div>
      ))}
      {step(3, "Once sent, record it", (
        <div className="flex flex-wrap items-center gap-2">
          <TextInput value={reference} onChange={setReference} placeholder="Bank / Stripe reference (optional)" className="w-[260px]" data-testid="transfer-reference" />
          <Button variant="admin_primary" disabled={!!busy} onClick={markPaid} data-testid="transfer-mark-paid">{busy === "mark_posted" ? "Saving…" : "Mark paid"}</Button>
          <Button variant="admin_secondary" disabled={!!busy} onClick={() => act("mark_failed", { reason: "Transfer failed or not made" })} data-testid="transfer-failed">Failed</Button>
        </div>
      ))}
      </>
      )}
    </div>
  )
}

export default function PayoutRuns({ orgNames = {}, onOpenVendor }) {
  const [plan, setPlan] = useState(null)
  const [list, setList] = useState(null)
  const [busy, setBusy] = useState("")
  const [invoiceOf, setInvoiceOf] = useState(null)
  // the open tab, in the url: it survives a reload
  const [tab, setTab] = useUrlParam("payouts_tab", { default: "ready" })
  const t = usePortalTexts(PAYOUTS)
  const name = (id, fallback) => orgNames[id] || fallback || id

  const load = () => {
    PayoutsAdmin("plan").then((res) => setPlan(res?.plan || [])).catch(() => setPlan([]))
    PayoutsList({}).then((res) => setList(res?.rows || [])).catch(() => setList([]))
  }
  useEffect(load, [])
  // a payout made or moved on anywhere (another admin, Stripe's report), or a sale that changes
  // what a vendor is owed: the lists reload (aldoo-na store/broadcast, the platform's room).
  // A sale's earnings are written just after it is announced: the reload waits a moment for them.
  const settle = useRef(null)
  useEffect(() => () => clearTimeout(settle.current), [])
  useStoreEvents({
    events: ["store:payout", "store:sale", "store:refund"],
    onEvent: ({ event }) => {
      clearTimeout(settle.current)
      settle.current = setTimeout(load, event === "store:payout" ? 0 : 1500)
    },
  })

  const ready = (plan || []).filter((p) => !p.blocker)
  const below = (plan || []).filter((p) => p.blocker === "below_minimum")
  const blocked = (plan || []).filter((p) => p.blocker && !["below_minimum", "nothing_to_pay"].includes(p.blocker))
  const waiting = [...below, ...blocked]
  // the vendors who owe Aldoo (cash on delivery commission beyond their online sales): nothing takes it until an admin charges it here
  const owing = (plan || []).filter((p) => p.amount < 0)
  const transfers = (list || []).filter((p) => ["created", "processing"].includes(p.status))
  const month = moment().format("YYYY-MM")
  const paidThisMonth = (list || []).filter((p) => p.status === "posted" && moment(p.postedAt).format("YYYY-MM") === month)
  const sum = (rows) => rows.reduce((total, p) => total + (p.amount || 0), 0)

  // a payout made for the vendor's sales due now: it becomes a transfer to make (below)
  const createPayout = (entry) =>
    showPromptDialog({
      variant: "admin",
      title: `Create a ${money(entry.amount, entry.currency)} payout to ${name(entry.orgId, entry.orgName)}?`,
      message: "Its sales are set aside for this payout. Then make the transfer and mark it paid.",
      confirmText: "Create payout",
      cancelText: "Cancel",
      onConfirm: async () => {
        setBusy(entry.orgId)
        const res = await PayoutsAdmin("pay_now", { orgId: entry.orgId }).catch((e) => ({ error: e?.message }))
        setBusy("")
        if (res?.error) return showErrorToast(res.error)
        if (!res.paid?.length) return showErrorToast("No payout made", (res.skipped || []).map((x) => REASON[x.reason] || x.reason).join(", "))
        showSuccessToast(`Payout created: ${money(res.paid[0].amount, res.paid[0].currency)} — now make the transfer`)
        load()
      },
    })
  // what a vendor owes, charged on this admin's approval: its card, its bank account, else a manual payment (its proforma)
  const methodText = (m) => (!m || m.type === "manual" ? "a manual payment: the vendor pays from its proforma (card, bank account or transfer) within 14 days" : m.type === "sepa_debit" ? `a debit of its bank account •••• ${m.last4}` : `its ${m.brand || "card"} •••• ${m.last4}`)
  const chargeVendor = (entry) =>
    showPromptDialog({
      variant: "admin",
      title: `Charge ${money(-entry.amount, entry.currency)} to ${name(entry.orgId, entry.orgName)}?`,
      message: `Aldoo's commission on its cash on delivery sales beyond what its online sales covered (${entry.count} items). Taken by ${methodText(entry.chargeMethod)}. Nothing is charged without this.`,
      confirmText: entry.chargeMethod?.type === "manual" ? "Open the payment" : "Charge",
      cancelText: "Cancel",
      onConfirm: async () => {
        setBusy(`charge:${entry.orgId}`)
        const res = await PayoutsAdmin("charge", { orgId: entry.orgId }).catch((e) => ({ error: e?.message }))
        setBusy("")
        if (res?.error) return showErrorToast(res.error)
        const c = res.charged?.[0]
        if (!c) return showErrorToast("Nothing charged", (res.skipped || []).map((x) => REASON[x.reason] || x.reason).join(", ") || "nothing is owed now")
        const said = { succeeded: "charged", processing: "bank debit started (a few business days)", awaiting: "payment opened — the vendor pays from its proforma", failed: "declined — retried on days 1, 3 and 5", canceled: "nothing owed any more" }
        ;(c.status === "failed" ? showErrorToast : showSuccessToast)(`${name(entry.orgId, entry.orgName)}: ${money(c.amount || -entry.amount, c.currency)} ${said[c.status] || c.status}`)
        load()
      },
    })
  const createAll = () =>
    showPromptDialog({
      variant: "admin",
      title: `Create payouts for ${ready.length} ${ready.length === 1 ? "vendor" : "vendors"}?`,
      message: `${ready.map((p) => `${name(p.orgId, p.orgName)} ${money(p.amount, p.currency)}`).join(", ")} — each becomes a transfer to make.`,
      confirmText: "Create payouts",
      cancelText: "Cancel",
      onConfirm: async () => {
        setBusy("all")
        const res = await PayoutsAdmin("run").catch((e) => ({ error: e?.message }))
        setBusy("")
        if (res?.error) return showErrorToast(res.error)
        showSuccessToast(`${res.paid?.length || 0} payouts created, ${res.skipped?.length || 0} skipped`)
        load()
      },
    })

  return (
    <div className="flex flex-col gap-4" data-testid="payout-runs">
      {/* where the money stands: due and payable, being transferred, still accumulating, paid this month */}
      <div className="flex flex-wrap gap-3" data-testid="payout-summary">
        <SummaryCard label="Ready to pay" count={ready.length} amount={sum(ready)} currency={ready[0]?.currency} sub="over the minimum: create their payouts" tone={ready.length ? "border-admin_success/50" : ""} testId="summary-ready" />
        <SummaryCard label="Transfers to make" unit="payout" count={transfers.length} amount={sum(transfers)} currency={transfers[0]?.currency} sub="payouts created, not yet marked paid" tone={transfers.length ? "border-admin_primary/50" : ""} testId="summary-transfers" />
        <SummaryCard label="Below the minimum" count={below.length} amount={sum(below)} currency={below[0]?.currency} sub="due, waiting to reach the minimum" testId="summary-below" />
        <SummaryCard label="Owed to Aldoo" count={owing.length} amount={-sum(owing)} currency={owing[0]?.currency} sub="charged only once you review them" tone={owing.length ? "border-admin_error/50" : ""} testId="summary-owing" />
        <SummaryCard label={`Paid in ${moment().format("MMMM")}`} unit="payout" count={paidThisMonth.length} amount={sum(paidThisMonth)} currency={paidThisMonth[0]?.currency} testId="summary-paid" />
      </div>

      <div className="bg-admin_paper rounded-xl space-y-4 p-4">
        <Tabs
          variant="admin"
          value={tab}
          onChange={setTab}
          styleOverrides={{
            tabActive: "bg-admin_neutral_1/70 text-admin_text border-b-2 border-admin_neutral_3",
          }}
          tabs={[
            { value: "ready", label: <TabLabel text="Ready to pay" count={ready.length} tone="bg-admin_success text-white" /> },
            { value: "transfers", label: <TabLabel text="Transfers to make" count={transfers.length} tone="bg-admin_primary text-white" /> },
            { value: "waiting", label: <TabLabel text="Not payable yet" count={waiting.length} tone="bg-amber-500 text-white" /> },
            { value: "charges", label: <TabLabel text="Charges to review" count={owing.length} tone="bg-admin_error text-white" /> },
            { value: "history", label: "Payout history" },
            { value: "reports", label: "Payout reports for the accountant" },
          ]}
        />
      

        {tab === "charges" && (
          <Section
            title="Charges to review"
            subtitle="Vendors who owe Aldoo: the commission on their cash on delivery sales beyond what their online sales covered. No run and no payout takes it — it is charged only when you press Charge, after reviewing the vendor."
            actions={<Button variant="admin_secondary" onClick={load}>Refresh</Button>}
          >
            {!plan ? (
              <Empty>Loading…</Empty>
            ) : (
              <Table
                rows={owing.map((p) => withSearch(p, name(p.orgId, p.orgName)))}
                placeholder="Search vendors"
                empty="No vendor owes Aldoo right now"
                renderRow={(p) => (
                  <div key={`${p.orgId}:${p.currency}`} className="flex flex-wrap items-center gap-4 py-2 text-sm border-b border-admin_bordered/60" data-testid="charge-review-row">
                    <Button variant="admin_primary" className="h-8 w-[140px] px-3 text-xs" disabled={!!busy} onClick={() => chargeVendor(p)} data-testid="charge-review">
                      {busy === `charge:${p.orgId}` ? "Charging…" : p.chargeMethod?.type === "manual" ? "Open payment" : "Charge"}
                    </Button>
                    <span className="w-[130px] text-right font-semibold tabular-nums text-admin_error">{money(-p.amount, p.currency)}</span>
                    <button type="button" className="min-w-[160px] text-left font-medium hover:underline" onClick={() => onOpenVendor?.(p.orgId)}>{name(p.orgId, p.orgName)}</button>
                    <span className="flex-1 text-xs text-admin_text_secondary">
                      {p.count} items · commission {money(p.commission, p.currency)} · {p.chargeMethod?.type === "card" ? `${p.chargeMethod.brand || "card"} •••• ${p.chargeMethod.last4}` : p.chargeMethod?.type === "sepa_debit" ? `bank account •••• ${p.chargeMethod.last4}` : "no card or debit: manual payment (proforma)"}
                    </span>
                  </div>
                )}
              />
            )}
          </Section>
        )}

        {tab === "ready" && (
          <Section
            title="Ready to pay"
            subtitle="Vendors whose due sales reached their minimum payout. Creating a payout sets those sales aside; it then waits in Transfers to make."
            actions={
              <div className="flex gap-2">
                <Button variant="admin_secondary" onClick={load}>Refresh</Button>
                {ready.length > 1 && (
                  <Button variant="admin_primary" disabled={!!busy} onClick={createAll} data-testid="payout-create-all">
                    {busy === "all" ? "Creating…" : `Create all (${ready.length})`}
                  </Button>
                )}
              </div>
            }
          >
            {!plan ? (
              <Empty>Loading…</Empty>
            ) : (
              <Table
                rows={ready.map((p) => withSearch(p, name(p.orgId, p.orgName)))}
                placeholder="Search vendors"
                empty="No vendor is over their minimum right now"
                renderRow={(p) => (
                  <div key={p._id} className="flex flex-wrap items-center gap-4 py-2 text-sm border-b border-admin_bordered/60" data-testid="payout-ready-row">
                    {/* the action first, then the amount in a fixed column (room for six figures), the vendor, the details */}
                    <Button variant="admin_primary" className="h-8 w-[120px] px-3 text-xs" disabled={!!busy} onClick={() => createPayout(p)} data-testid="payout-create">
                      {busy === p.orgId ? "Creating…" : "Create payout"}
                    </Button>
                    <span className="w-[130px] text-right font-semibold tabular-nums">{money(p.amount, p.currency)}</span>
                    <button type="button" className="min-w-[160px] text-left font-medium hover:underline" onClick={() => onOpenVendor?.(p.orgId)}>{name(p.orgId, p.orgName)}</button>
                    <span className="flex-1 text-xs text-admin_text_secondary">{p.count} items · {p.stripeAccount ? `Stripe account ${p.stripeAccount.name || p.stripeAccount.id}` : `IBAN •••• ${p.ibanLast4 || "????"}`} · minimum {money(p.minimum || 0, p.currency)}</span>
                  </div>
                )}
              />
            )}
          </Section>
        )}

        {tab === "transfers" && (
          <Section title="Transfers to make" subtitle="Each created payout: send the money, then record it. Marking it paid issues the vendor's payout report and emails them.">
            {!list ? (
              <Empty>Loading…</Empty>
            ) : (
              <Table
                rows={transfers.map((p) => withSearch(p, name(String(p.orgId), p.orgName), p.transferMessage))}
                placeholder="Search transfers by vendor, amount or message"
                empty="No transfers waiting"
                itemsPerPage={5}
                renderRow={(p) => (
                  <div key={p._id} className="py-1.5">
                    <TransferCard payout={p} vendor={name(String(p.orgId), p.orgName)} onDone={load} onOpenVendor={onOpenVendor} />
                  </div>
                )}
              />
            )}
          </Section>
        )}

        {tab === "waiting" && (
          <Section title="Not payable yet" subtitle="Due sales under the vendor's minimum, or a vendor that cannot be paid yet (no signed contract, payout details incomplete)">
            {!plan ? (
              <Empty>Loading…</Empty>
            ) : (
              <Table
                rows={waiting.map((p) => withSearch(p, name(p.orgId, p.orgName), REASON[p.blocker] || p.blocker))}
                placeholder="Search vendors or reasons"
                empty="Every vendor with due sales can be paid"
                renderRow={(p) => (
                  <div key={p._id} className="flex flex-wrap items-center gap-3 py-2 text-sm border-b border-admin_bordered/60" data-testid="payout-waiting-row">
                    <button type="button" className="flex-1 min-w-[180px] text-left font-medium hover:underline" onClick={() => onOpenVendor?.(p.orgId)}>{name(p.orgId, p.orgName)}</button>
                    {p.blocker === "below_minimum" && p.minimum > 0 ? (
                      <span className="flex items-center gap-2 w-[260px]">
                        <span className="flex-1 h-2 rounded-full bg-admin_neutral_1 overflow-hidden">
                          <span className="block h-full bg-admin_primary" style={{ width: `${Math.min(100, Math.round((Math.max(0, p.amount) / p.minimum) * 100))}%` }} />
                        </span>
                        <span className="text-xs text-admin_text_secondary tabular-nums">{money(p.amount, p.currency)} / {money(p.minimum, p.currency)}</span>
                      </span>
                    ) : (
                      <span className="w-[260px] text-xs text-amber-700">{REASON[p.blocker] || p.blocker} · {money(p.amount, p.currency)}</span>
                    )}
                  </div>
                )}
              />
            )}
          </Section>
        )}

        {tab === "history" && (
          <Section title="Payout history" subtitle="Every payout to a vendor, newest first">
            {!list ? (
              <Empty>Loading…</Empty>
            ) : (
              <Table
                rows={list.map((p) => withSearch(p, name(String(p.orgId), p.orgName), dateOnly(p.postedAt || p.runAt || p.createdAt), p.status, p.invoiceNumber, p.reference, p.providerPaymentId, p.transferMessage))}
                placeholder="Search by vendor, report number, amount, date, status or reference"
                empty="No payouts yet"
                renderRow={(p) => (
                  <div key={p._id} className="flex flex-wrap items-center gap-3 py-2 text-sm border-b border-admin_bordered/60" data-testid="payout-made-row">
                    <span className="w-[110px]">{dateOnly(p.postedAt || p.runAt || p.createdAt)}</span>
                    <button type="button" className="flex-1 min-w-[180px] text-left hover:underline" onClick={() => onOpenVendor?.(String(p.orgId))}>{name(String(p.orgId), p.orgName)}</button>
                    <Chip className={CHIP[p.status] || "bg-admin_neutral_1"}>{p.status}</Chip>
                    <span className="text-xs text-admin_text_secondary w-[90px]">{p.period} · {p.method}</span>
                    <span className="w-[110px] text-right font-semibold">{money(p.amount, p.currency)}</span>
                    <PayoutDocuments payout={p} onOpenInvoice={setInvoiceOf} t={t} />
                  </div>
                )}
              />
            )}
          </Section>
        )}

        {tab === "reports" && (
          <>
            <InvoicesExport />
            <Section title="Issued payout reports" subtitle="Every payout report, newest first: open it, or find one by its number, vendor, amount or date">
              {!list ? (
                <Empty>Loading…</Empty>
              ) : (
                <Table
                  rows={list.filter((p) => p.invoiceNumber).map((p) => withSearch(p, p.invoiceNumber, name(String(p.orgId), p.orgName), dateOnly(p.postedAt || p.runAt || p.createdAt)))}
                  placeholder="Search reports by number, vendor, amount or date"
                  empty="No payout reports yet"
                  renderRow={(p) => (
                    <div key={p._id} className="flex flex-wrap items-center gap-3 py-2 text-sm border-b border-admin_bordered/60" data-testid="payout-report-row">
                      <button type="button" className="w-[170px] text-left font-medium text-admin_primary hover:underline" onClick={() => setInvoiceOf(p)}>{p.invoiceNumber}</button>
                      <span className="w-[110px]">{dateOnly(p.postedAt || p.runAt || p.createdAt)}</span>
                      <button type="button" className="flex-1 min-w-[180px] text-left hover:underline" onClick={() => onOpenVendor?.(String(p.orgId))}>{name(String(p.orgId), p.orgName)}</button>
                      <span className="w-[110px] text-right font-semibold">{money(p.amount, p.currency)}</span>
                    </div>
                  )}
                />
              )}
            </Section>
          </>
        )}
        <InvoicePdf payout={invoiceOf} onClose={() => setInvoiceOf(null)} />
      </div>
    </div>
  )
}
